'use client';

import { useState, useEffect, useRef, Fragment, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { parseCookies } from 'nookies';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, Book, Search, Users, User, LogIn, LogOut, Crown, RefreshCw, Bell, Menu, X, List, Globe, History, CreditCard, Layers } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { api, API_CONFIG, unwrapApiResponse } from '@/utils/api';
import { getUserImageUrl, getTagImageUrl, DEFAULT_AVATAR } from '@/utils/image-utils';
import DiscordIcon from '@/components/icons/DiscordIcon';
import NotificationsModal from '@/components/notifications/NotificationsModal';
import { FORMATO_BUSCA_TODOS_QUERY } from '@/components/FormatoPicker';
import { useNotifications } from '@/hooks/useNotifications';
import { useReadingTabs } from '@/hooks/useReadingTabs';
import ReadingTabsBottomSheet from '@/components/reading-tabs/ReadingTabsBottomSheet';
import ReadingTabsMobileChapterTrigger from '@/components/reading-tabs/ReadingTabsMobileChapterTrigger';

const DISCORD_URL = 'https://discord.gg/mediocretoons';
const SIDE_MENU_CLOSE_SWIPE_PX = 48;

type PlanLike = { id: number; name: string; nome?: string; pricing: number; tag_imagem?: string | null };

function SideMenuSectionTitle({ children }: { children: string }) {
    return (
        <h3 className="px-3 mt-6 first:mt-1 mb-1.5 text-[12px] font-medium text-white/38">
            {children}
        </h3>
    );
}

export default function MobileBottomNav() {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { isAuthenticated, user, login, logout, isLoading: authLoading } = useAuth();
    const { requireVipToAccess } = useSiteConfig();
    const { unreadCount, fetchUnreadCount } = useNotifications();
    const readingTabs = useReadingTabs();
    const canAccessSite = !!user?.is_vip || !requireVipToAccess;
    const [checkingVip, setCheckingVip] = useState(false);
    const [showNotificationsModal, setShowNotificationsModal] = useState(false);
    const [showReadingTabsModal, setShowReadingTabsModal] = useState(false);
    const [isVisible, setIsVisible] = useState(true);
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    const [resolvedPlanHeader, setResolvedPlanHeader] = useState<{ nome: string; tag_imagem?: string | null } | null>(null);
    const lastScrollY = useRef(0);
    const sideMenuTouchStartX = useRef(0);
    const sideMenuTouchStartY = useRef(0);
    const vipExpiresAt = user?.vip_acaba_em || user?.vip_acabaem;
    const plan = (user?.plan as { name?: string; nome?: string; isActive?: boolean; tag_imagem?: string | null } | undefined) ?? undefined;

    // PWA: esconder navbar ao scrollar pra baixo, mostrar ao scrollar pra cima
    useEffect(() => {
        if (typeof window === 'undefined' || window.innerWidth > 1024) {
            return;
        }

        const isPWA = window.matchMedia('(display-mode: standalone)').matches;
        if (!isPWA) return; // Só no PWA

        let ticking = false;
        let touchStartY = 0;

        const updateVisibility = (scrollingDown: boolean) => {
            if (scrollingDown) setIsVisible(false);
            else setIsVisible(true);
        };

        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    const currentScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;

                    if (currentScrollY <= 10) {
                        setIsVisible(true);
                        lastScrollY.current = currentScrollY;
                        ticking = false;
                        return;
                    }

                    const scrollDifference = currentScrollY - lastScrollY.current;

                    if (scrollDifference > 5) {
                        setIsVisible(false);
                    } else if (scrollDifference < -5) {
                        setIsVisible(true);
                    }

                    lastScrollY.current = currentScrollY;
                    ticking = false;
                });
                ticking = true;
            }
        };

        const handleTouchStart = (e: TouchEvent) => {
            touchStartY = e.touches[0].clientY;
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (window.scrollY <= 10) {
                setIsVisible(true);
                return;
            }
            const currentY = e.touches[0].clientY;
            const diff = touchStartY - currentY;
            touchStartY = currentY;

            if (Math.abs(diff) < 8) return;

            if (diff > 0) {
                updateVisibility(true);
            } else {
                updateVisibility(false);
            }
        };

        lastScrollY.current = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
        window.addEventListener('scroll', handleScroll, { passive: true });
        document.addEventListener('touchstart', handleTouchStart, { passive: true });
        document.addEventListener('touchmove', handleTouchMove, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('touchstart', handleTouchStart);
            document.removeEventListener('touchmove', handleTouchMove);
        };
    }, []);

    useEffect(() => {
        if (!showMoreMenu && !showReadingTabsModal) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setShowMoreMenu(false);
                setShowReadingTabsModal(false);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [showMoreMenu, showReadingTabsModal]);

    useEffect(() => {
        let cancelled = false;
        if (!user?.is_vip) {
            setResolvedPlanHeader(null);
            return () => {
                cancelled = true;
            };
        }

        const localPlanName = (plan?.nome ?? plan?.name ?? '').trim();
        const localHasTag = !!(plan?.tag_imagem && String(plan.tag_imagem).trim());
        if (localHasTag && localPlanName && localPlanName.toLowerCase() !== 'vip') {
            setResolvedPlanHeader({ nome: localPlanName, tag_imagem: plan?.tag_imagem ?? null });
            return () => {
                cancelled = true;
            };
        }

        api.get('planos', { requiresAuth: false })
            .then((res) => {
                if (cancelled) return;
                const raw = unwrapApiResponse<unknown>(res);
                const list = Array.isArray(raw)
                    ? raw
                    : raw && typeof raw === 'object' && Array.isArray((raw as { data?: unknown[] }).data)
                      ? ((raw as { data?: unknown[] }).data ?? [])
                      : [];
                const plans: PlanLike[] = list
                    .map((p) => {
                        const row = p as Partial<PlanLike> & { id?: number; pricing?: number; nome?: string; name?: string };
                        if (!Number.isFinite(Number(row.id))) return null;
                        return {
                            id: Number(row.id),
                            name: String(row.name ?? row.nome ?? 'Plano'),
                            nome: typeof row.nome === 'string' ? row.nome : undefined,
                            pricing: Number(row.pricing ?? 0),
                            tag_imagem: typeof row.tag_imagem === 'string' ? row.tag_imagem : null,
                        } as PlanLike;
                    })
                    .filter((x): x is PlanLike => x != null);
                if (plans.length === 0) return;

                const currentByName = localPlanName
                    ? plans.find((p) => {
                          const dn = p.nome ?? p.name;
                          return localPlanName === dn || localPlanName === p.name || localPlanName === p.nome;
                      })
                    : undefined;
                const fallback = !currentByName ? plans.reduce((min, p) => (p.pricing < min.pricing ? p : min), plans[0]) : undefined;
                const selected = currentByName ?? fallback;
                if (!selected) return;
                setResolvedPlanHeader({
                    nome: (selected.nome ?? selected.name ?? 'VIP').trim() || 'VIP',
                    tag_imagem: selected.tag_imagem ?? null,
                });
            })
            .catch(() => { /* mantém fallback */ });

        return () => {
            cancelled = true;
        };
    }, [plan?.name, plan?.nome, plan?.tag_imagem, user?.is_vip]);

    const recordSideMenuTouchStart = (e: React.TouchEvent) => {
        sideMenuTouchStartX.current = e.touches[0].clientX;
        sideMenuTouchStartY.current = e.touches[0].clientY;
    };

    const maybeCloseSideMenuBySwipe = (e: React.TouchEvent) => {
        const t = e.changedTouches[0];
        const dx = t.clientX - sideMenuTouchStartX.current;
        const dy = t.clientY - sideMenuTouchStartY.current;
        if (dx > SIDE_MENU_CLOSE_SWIPE_PX && dx > Math.abs(dy) * 0.85) {
            setShowMoreMenu(false);
        }
    };

    const closeSideMenuAnd = (fn: () => void) => {
        setShowMoreMenu(false);
        fn();
    };

    // Na leitura não há barra inferior completa — só atalho às abas rápidas
    if (pathname.startsWith('/capitulo/')) {
        return <ReadingTabsMobileChapterTrigger />;
    }

    const isVip = !!user?.is_vip;

    const handleCheckVip = async () => {
        if (checkingVip || authLoading) return;

        setCheckingVip(true);
        try {
            const userData = unwrapApiResponse<any>(await api.get('usuarios/me', { requiresAuth: true }));
            if (userData) {
                const cookies = parseCookies();
                const token = cookies.token;
                if (token) {
                    login(token, userData);
                }
            }
        } catch (error) {
            console.error('Erro ao verificar VIP:', error);
        } finally {
            setCheckingVip(false);
        }
    };

    // Primeira seção do menu (principal) — Notificações no lugar de Ranking
    const mainItems = [
        { href: '/inicio', label: 'Início', icon: Home, show: true },
        { href: `/pesquisar?formato=${encodeURIComponent(FORMATO_BUSCA_TODOS_QUERY)}&pagina=1`, label: 'Pesquisar', icon: Search, show: true },
        { href: '/colecao', label: 'Coleção', icon: Book, show: isAuthenticated && canAccessSite },
        { href: '#reading-tabs', label: 'Abas', icon: Layers, show: canAccessSite, isModal: true, modalType: 'reading-tabs' },
        { href: '#notifications', label: 'Notificações', icon: Bell, show: isAuthenticated && canAccessSite, isModal: true, modalType: 'notifications' },
    ];

    const moreItems = [
        { href: '/comunidade', label: 'Comunidade', icon: Users, show: isAuthenticated && canAccessSite },
        { href: '/ranking', label: 'Ranking', icon: Crown, show: isAuthenticated && canAccessSite },
        { href: '/historico', label: 'Histórico', icon: History, show: isAuthenticated && canAccessSite },
        { href: '/listas', label: 'Listas', icon: List, show: isAuthenticated && canAccessSite },
        { href: '/planos', label: 'Planos', icon: CreditCard, show: isAuthenticated && canAccessSite },
    ];

    // Items para mostrar baseado no estado
    const visibleMainItems = mainItems.filter(item => item.show);
    const visibleMoreItems = moreItems.filter(item => item.show);

    // Se não logado: só Entrar (English só quando logado)
    const notAuthItems = [
        { href: '/entrar', label: 'Entrar', icon: LogIn },
    ];

    // Se não pode acessar (exige VIP e não tem): Planos, Perfil, Sair
    const notVipItems = [
        { href: '/planos', label: 'Planos', icon: CreditCard },
        { href: '/meu-perfil', label: 'Perfil', icon: User },
        { href: '#sair', label: 'Sair', icon: LogOut, isLogout: true },
    ];

    // Determinar quais items mostrar na barra inferior (principal)
    const getItemsToRender = () => {
        if (!isAuthenticated) {
            return notAuthItems;
        }
        if (!canAccessSite) {
            return notVipItems;
        }
        return [...visibleMainItems, { href: '#more', label: 'Menu', icon: Menu, isMoreButton: true }];
    };

    const itemsToRender = getItemsToRender();

    const readingTabRouteCapId = useMemo(() => {
        const m = pathname.match(/^\/capitulo\/(\d+)/);
        return m && m[1] ? Number(m[1]) : null;
    }, [pathname]);

    const explorationOrder = ['/comunidade', '/listas', '/planos', '/ranking', '/historico'] as const;
    const explorationItems = explorationOrder
        .map((href) => visibleMoreItems.find((i) => i.href === href))
        .filter((x): x is (typeof visibleMoreItems)[number] => Boolean(x));
    const drawerAvatarUrl = user ? getUserImageUrl(user.usr_id, user.usr_imagem) : null;
    const drawerDisplayName = (user?.usr_nick || user?.usr_nome || 'Conta').trim();
    const headerPlanName = resolvedPlanHeader?.nome ?? plan?.nome ?? plan?.name ?? '';
    const headerPlanTag = resolvedPlanHeader?.tag_imagem ?? plan?.tag_imagem ?? null;
    const planTagUrl = getTagImageUrl(headerPlanTag);

    const sideMenuRowClass =
        'w-full flex items-center gap-3.5 rounded-2xl px-4 py-3.5 text-left text-base text-white/90 hover:bg-white/[0.06] active:bg-white/[0.08] transition-colors min-h-[52px]';

    const getItemKey = (item: any, index: number) =>
        `nav-${index}-${item.href || item.modalType || (item.isLogout ? 'sair' : '') || item.label || index}`;

    const formatVipExpiration = (dateStr?: string) => {
        if (!dateStr) return null;
        const dt = new Date(dateStr);
        if (Number.isNaN(dt.getTime())) return null;
        return dt.toLocaleDateString('pt-BR');
    };

    const renderItem = (item: any, index: number) => {
        const Icon = item.icon;
        const itemPath = typeof item.href === 'string' ? item.href.split('?')[0] : '';
        const isActive = itemPath !== '' && pathname === itemPath && !item.isModal;
        const baseItemClass = 'flex-1 min-w-0 flex flex-col items-center justify-center gap-1 h-full transition-colors';

        // Botão "Menu" para abrir drawer lateral
        if (item.isMoreButton) {
            return (
                <button
                    type="button"
                    onClick={() => setShowMoreMenu(true)}
                    className={`${baseItemClass} text-white/60 active:text-orange-500`}
                >
                    <Icon size={20} />
                    <span className="text-[10px] whitespace-nowrap font-medium">
                        {item.label}
                    </span>
                </button>
            );
        }

        // Special render for non-link buttons (modals)
        if (item.isModal) {
            const isNotifications = item.modalType === 'notifications';
            const isReadingTabs = item.modalType === 'reading-tabs';
            const isActiveState =
                (isNotifications && showNotificationsModal) || (isReadingTabs && showReadingTabsModal);
            const iconOnly = item.iconOnly;

            return (
                <button
                    type="button"
                    onClick={() => {
                        if (item.modalType === 'notifications') setShowNotificationsModal(true);
                        if (item.modalType === 'reading-tabs') setShowReadingTabsModal(true);
                    }}
                    className={`
                        ${baseItemClass}
                        transition-colors relative
                        ${isActiveState ? 'text-orange-500' : 'text-white/60 active:text-orange-500'}
                    `}
                    title={isNotifications ? 'Notificações' : isReadingTabs ? 'Abas de leitura' : undefined}
                >
                    <div className="relative">
                        <Icon size={20} className={isActiveState ? 'scale-110' : ''} />
                        {isNotifications && unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-orange-500 rounded-full border-2 border-neutral-950"></span>
                        )}
                    </div>
                    {!iconOnly && (
                        <span className={`text-[10px] whitespace-nowrap ${isActiveState ? 'font-semibold' : 'font-medium'}`}>
                            {item.label}
                        </span>
                    )}
                </button>
            );
        }

        if (item.isLogout) {
            return (
                <button
                    type="button"
                    onClick={() => logout()}
                    className={`${baseItemClass} text-white/60 active:text-red-400`}
                >
                    <Icon size={20} />
                    <span className="text-[10px] whitespace-nowrap font-medium">{item.label}</span>
                </button>
            );
        }

        const href = item.href;
        const hrefStr = typeof href === 'string' ? href : (href && typeof href === 'object' ? String(href) : '/');

        return (
            <Link
                to={hrefStr}
                className={`
                    ${baseItemClass} relative
                    ${isActive ? 'text-orange-500' : 'text-white/60 active:text-orange-500'}
                `}
            >
                <Icon size={20} className={isActive ? 'scale-110' : ''} />
                <span className={`text-[10px] whitespace-nowrap ${isActive ? 'font-semibold' : 'font-medium'}`}>
                    {item.label}
                </span>
                {isActive && (
                    <motion.div
                        layoutId="activeIndicator"
                        className="absolute -bottom-[1px] left-1/2 -translate-x-1/2 w-8 h-0.5 bg-orange-500 rounded-full"
                    />
                )}
            </Link>
        );
    };

    return (
        <motion.nav
            initial={false}
            animate={{ y: isVisible ? 0 : '150%', opacity: isVisible ? 1 : 0 }}
            transition={{ type: 'tween', duration: 0.3, ease: 'easeInOut' }}
            className={`pwa-mobile-nav fixed bottom-0 left-0 right-0 z-[100] bg-neutral-950 border-t border-white/10 lg:hidden safe-area-inset-bottom ${!isVisible ? 'pointer-events-none' : ''}`}
        >
            <div className="flex items-center h-16 w-full overflow-hidden px-1">
                <div className={`flex items-center w-full h-full gap-1 ${!isAuthenticated ? 'max-w-[220px] mx-auto' : ''}`}>
                    {itemsToRender.map((item, index) => (
                        <Fragment key={getItemKey(item, index)}>
                            {renderItem(item, index)}
                        </Fragment>
                    ))}

                    {/* Botão Verificar VIP (Apenas se não for VIP e estiver logado) */}
                    {isAuthenticated && !canAccessSite && (
                        <button
                            onClick={handleCheckVip}
                            disabled={checkingVip}
                            className="flex-1 min-w-0 flex flex-col items-center justify-center gap-1 h-full text-white/60 active:text-orange-500 disabled:opacity-50"
                        >
                            <RefreshCw size={20} className={checkingVip ? 'animate-spin' : ''} />
                            <span className="text-[10px] whitespace-nowrap font-medium">
                                {checkingVip ? 'Verific...' : 'Verificar'}
                            </span>
                        </button>
                    )}
                </div>
            </div>

            <AnimatePresence>
                {showMoreMenu && (
                    <>
                        <motion.div
                            key="side-menu-backdrop"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.12, ease: 'easeOut' }}
                            className="fixed inset-0 z-[110] bg-black/40 lg:hidden will-change-[opacity]"
                            onClick={() => setShowMoreMenu(false)}
                            onTouchStart={recordSideMenuTouchStart}
                            onTouchEnd={maybeCloseSideMenuBySwipe}
                            aria-hidden
                        />
                        <motion.aside
                            key="side-menu-panel"
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'tween', duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
                            className="fixed right-0 top-0 bottom-0 z-[111] w-[min(80vw,272px)] lg:hidden flex flex-col min-h-0 bg-neutral-950 border-l border-white/[0.06] touch-pan-y will-change-transform transform-gpu contain-layout"
                            style={{
                                paddingTop: 'env(safe-area-inset-top)',
                                paddingBottom: 'env(safe-area-inset-bottom)',
                            }}
                            role="dialog"
                            aria-modal="true"
                            aria-label="Menu"
                            onTouchStart={recordSideMenuTouchStart}
                            onTouchEnd={maybeCloseSideMenuBySwipe}
                        >
                            <header className="shrink-0 px-4 pt-4 pb-4 border-b border-white/[0.06]">
                                <div className="flex items-center gap-3.5">
                                    <div className="w-14 h-14 rounded-full overflow-hidden bg-white/[0.06] shrink-0">
                                        {drawerAvatarUrl ? (
                                            <img src={drawerAvatarUrl} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-white/30">
                                                <User size={28} strokeWidth={1.5} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5 min-w-0">
                                            <p className="text-base font-semibold text-white/95 truncate leading-tight">{drawerDisplayName}</p>
                                            {user?.is_vip && headerPlanName && planTagUrl ? (
                                                <span className="inline-flex items-center justify-center rounded-sm bg-amber-400/25 px-0.5 py-0.5 shrink-0">
                                                    <img src={planTagUrl} alt="" className="w-4 h-4 object-contain rounded-[2px]" loading="lazy" />
                                                </span>
                                            ) : null}
                                        </div>
                                        {user?.is_vip && formatVipExpiration(vipExpiresAt) ? (
                                            <p className="mt-1 text-xs text-orange-300/90">VIP até {formatVipExpiration(vipExpiresAt)}</p>
                                        ) : null}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setShowMoreMenu(false)}
                                        className="p-2.5 rounded-xl text-white/50 hover:text-white/85 hover:bg-white/[0.06] transition-colors shrink-0 -mr-1"
                                        aria-label="Fechar menu"
                                    >
                                        <X size={24} strokeWidth={2} />
                                    </button>
                                </div>
                            </header>

                            <div className="flex-1 flex flex-col min-h-0">
                                <nav className="flex-1 overflow-y-auto overscroll-contain px-2.5 pt-2 pb-3">
                                    <SideMenuSectionTitle>Explorar</SideMenuSectionTitle>
                                    <div className="flex flex-col gap-1">
                                        {explorationItems.map((item, index) => {
                                            const Icon = item.icon;
                                            return (
                                                <button
                                                    key={getItemKey(item, index)}
                                                    type="button"
                                                    onClick={() => closeSideMenuAnd(() => navigate(item.href))}
                                                    className={sideMenuRowClass}
                                                    aria-label={item.label}
                                                >
                                                    <span className="flex items-center justify-center w-11 h-11 rounded-xl shrink-0 bg-white/[0.05] text-white/45">
                                                        <Icon size={24} strokeWidth={1.75} />
                                                    </span>
                                                    <span className="font-medium leading-snug">{item.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <SideMenuSectionTitle>Conta</SideMenuSectionTitle>
                                    <div className="flex flex-col gap-1">
                                        <button
                                            type="button"
                                            onClick={() => closeSideMenuAnd(() => navigate('/meu-perfil'))}
                                            className={sideMenuRowClass}
                                            aria-label="Meu perfil"
                                        >
                                            <span className="flex items-center justify-center w-11 h-11 rounded-xl shrink-0 bg-white/[0.05] text-white/45">
                                                <User size={24} strokeWidth={1.75} />
                                            </span>
                                            <span className="font-medium leading-snug">Meu perfil</span>
                                        </button>
                                    </div>

                                    <SideMenuSectionTitle>Links</SideMenuSectionTitle>
                                    <div className="flex flex-col gap-1">
                                        <button
                                            type="button"
                                            onClick={() => closeSideMenuAnd(() => window.open(DISCORD_URL, '_blank', 'noopener,noreferrer'))}
                                            className={sideMenuRowClass}
                                            aria-label="Discord / Suporte"
                                        >
                                            <span className="flex items-center justify-center w-11 h-11 rounded-xl shrink-0 bg-white/[0.05] text-[#7289da]">
                                                <DiscordIcon size={24} className="text-[#7289da]" />
                                            </span>
                                            <span className="font-medium leading-snug">Discord / Suporte</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                closeSideMenuAnd(() =>
                                                    window.open(API_CONFIG.SITE_URL.replace(/\/$/, ''), '_blank', 'noopener,noreferrer'),
                                                )
                                            }
                                            className={sideMenuRowClass}
                                            aria-label="Abrir site"
                                        >
                                            <span className="flex items-center justify-center w-11 h-11 rounded-xl shrink-0 bg-white/[0.05] text-orange-400/90">
                                                <Globe size={24} strokeWidth={1.75} />
                                            </span>
                                            <span className="font-medium leading-snug">Abrir site</span>
                                        </button>
                                    </div>
                                </nav>

                                <div className="shrink-0 border-t border-white/[0.06] px-3 py-4 bg-neutral-950">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (!window.confirm('Tem certeza que deseja sair da conta?')) return;
                                            setShowMoreMenu(false);
                                            logout();
                                        }}
                                        className="w-full flex items-center justify-center gap-2.5 py-3.5 min-h-[52px] text-base font-medium text-red-400/75 hover:text-red-400 transition-colors rounded-2xl hover:bg-white/[0.05]"
                                    >
                                        <LogOut size={22} strokeWidth={2} />
                                        Sair
                                    </button>
                                </div>
                            </div>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>

            <NotificationsModal isOpen={showNotificationsModal} onClose={() => {
                setShowNotificationsModal(false);
                fetchUnreadCount();
            }} />
            <ReadingTabsBottomSheet
                open={showReadingTabsModal}
                onClose={() => setShowReadingTabsModal(false)}
                tabs={readingTabs}
                activeCapId={readingTabRouteCapId}
            />
        </motion.nav>
    );
}
